/**
 * Product Item Boost - Add to Cart Functionality
 * Handles adding products to cart when clicking size buttons
 * Used for search results with API
 */

(function() {
  'use strict';

  /**
   * Add variant to cart
   * @param {number|string} variantId - The variant ID to add
   * @param {number} qty - Quantity to add (default: 1)
   * @param {object} extraProps - Additional properties for the cart item
   * @returns {Promise<boolean>} - Success status
   */
  async function addVariantToCart(variantId, qty = 1, extraProps = null) {
    if (!variantId) {
      console.error('[Product Boost Cart] No variant ID provided');
      return false;
    }

    const url = (window.routes && window.routes.cartAddUrl ? window.routes.cartAddUrl : '/cart/add') + '.js';
    
    // Dispatch loading start event
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    const payload = { 
      id: Number(variantId), 
      quantity: Number(qty) 
    };
    
    if (extraProps && typeof extraProps === 'object') {
      payload.properties = extraProps;
    }

    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });

      // Dispatch loading end event
      document.dispatchEvent(new CustomEvent('theme:loading:end'));

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Unable to add to cart');
      }

      // Dispatch product added event
      document.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: {
          variant: { id: variantId },
          quantity: Number(qty)
        }
      }));

      return true;
    } catch (e) {
      console.error('[Product Boost Cart] Add to cart failed:', e);
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      return false;
    }
  }

  /**
   * Handle size button click
   * @param {Event} e - Click event
   */
  async function handleSizeButtonClick(e) {
    const sizeBtn = e.target.closest('.SizeSwatch');
    
    if (!sizeBtn) return;
    
    // Check if button is disabled
    if (sizeBtn.classList.contains('is-disabled') || sizeBtn.disabled) {
      return;
    }

    const variantId = sizeBtn.getAttribute('data-variant-id');
    
    if (!variantId) {
      console.warn('[Product Boost Cart] No variant ID found on size button');
      return;
    }

    // Disable button during add to cart
    sizeBtn.setAttribute('disabled', 'disabled');
    const originalText = sizeBtn.textContent.trim();
    
    // Optional: Show loading state
    // sizeBtn.textContent = 'Adding...';

    try {
      const success = await addVariantToCart(variantId, 1);

      if (success) {
        // Close mobile size footer if it's open
        const mobileFooter = document.getElementById('mobileSizeFooter');
        const customOverlay = document.querySelector('.custom-overlay');
        
        if (mobileFooter && mobileFooter.classList.contains('active')) {
          mobileFooter.classList.remove('active');
          mobileFooter.classList.remove('closing');
          if (customOverlay) {
            customOverlay.classList.remove('active');
          }
          console.log('[Product Boost Cart] Closed mobile footer after adding product to cart');
        }
        
        // Close the size box after successful add to cart
        const sizeBox = sizeBtn.closest('.available-variant-size');
        if (sizeBox) {
          sizeBox.classList.remove('is-open');
          // Also remove active states from icons and buttons
          const card = sizeBox.closest('.ProductItem');
          if (card) {
            const icon = card.querySelector('svg.icon.plus-minus');
            if (icon) icon.classList.remove('is-minus');
            const mobileBtn = card.querySelector('.show-size-for-mobile');
            if (mobileBtn) mobileBtn.classList.remove('is-active');
          }
        }
        
        // Optional: Show success feedback
        // sizeBtn.textContent = 'Added!';
        // setTimeout(() => {
        //   sizeBtn.textContent = originalText;
        //   sizeBtn.removeAttribute('disabled');
        // }, 1000);
        
        // Re-enable button after a short delay
        setTimeout(() => {
          sizeBtn.removeAttribute('disabled');
        }, 500);
      } else {
        // Re-enable button on failure
        sizeBtn.removeAttribute('disabled');
      }
    } catch (error) {
      console.error('[Product Boost Cart] Error adding to cart:', error);
      sizeBtn.removeAttribute('disabled');
    }
  }

  /**
   * Build sizes HTML from product data (similar to boost-pfs-filter.js)
   * @param {object} productData - Product data from API
   * @returns {string} - HTML string for sizes
   */
  function buildSizes(productData) {
    if (!productData) return '';
    
    let sizeOptIndex = null;
    if (Array.isArray(productData.options)) {
      productData.options.forEach((opt, i) => {
        const n = (opt && (opt.name || opt.label) || '').toString().trim().toLowerCase();
        if (n === 'size' && sizeOptIndex == null) sizeOptIndex = i + 1;
      });
    }

    if (sizeOptIndex == null && Array.isArray(productData.options_with_values)) {
      productData.options_with_values.forEach((opt, i) => {
        const n = (opt && (opt.name || opt.label) || '').toString().trim().toLowerCase();
        if (n === 'size' && sizeOptIndex == null) sizeOptIndex = i + 1;
      });
    }

    if (!sizeOptIndex) return '';
    
    let sizeValues = [];
    const owv = (productData.options_with_values || []).find(o => {
      const n = (o.name || o.label || '').toString().trim().toLowerCase();
      return n === 'size';
    });
    
    if (owv && Array.isArray(owv.values) && owv.values.length) {
      sizeValues = owv.values.map(v => v.title || v);
    } else if (Array.isArray(productData.variants)) {
      const set = new Set();
      productData.variants.forEach(v => {
        const key = (v['option' + sizeOptIndex] || '').toString().trim();
        if (key) set.add(key);
      });
      sizeValues = Array.from(set);
    }
    
    if (!sizeValues.length) return '';
    
    const variants = Array.isArray(productData.variants) ? productData.variants : [];
    const bySize = {};
    variants.forEach(v => {
      const sizeVal = (v['option' + sizeOptIndex] || '').toString().trim();
      if (!sizeVal) return;
      if (!bySize[sizeVal] || (!bySize[sizeVal].available && v.available)) {
        bySize[sizeVal] = v;
      }
    });
    
    let html = '<div class="VariantSizes" role="group" aria-label="Choose size">';
    sizeValues.forEach(val => {
      const sizeTitle = (val || '').toString().trim();
      const variant = bySize[sizeTitle];
      const available = variant ? !!variant.available : false;

      const classes = ['SizeSwatch'];
      if (!available) classes.push('is-disabled');

      html += `
        <button type="button"
                class="${classes.join(' ')}"
                data-size="${sizeTitle}"
                ${variant ? `data-variant-id="${variant.id}"` : ''}
                ${available ? 'aria-pressed="false"' : 'disabled aria-disabled="true"'}
                title="${sizeTitle}${available ? '' : ' - Out of stock'}" data-action="add-to-cart">
          ${sizeTitle}
          
        </button>`;
    });
    html += '</div>';

    return html;
  }

  /**
   * Build price HTML from product data
   * @param {object} productData - Product data from API
   * @param {object} variant - Selected variant
   * @returns {string} - HTML string for price
   */
  function buildPrice(productData, variant) {
    if (!variant) {
      variant = (productData.variants && productData.variants.length > 0) ? productData.variants[0] : null;
    }
    
    if (!variant) return '';
    
    const price = (variant.price / 100).toFixed(0);
    const compareAtPrice = variant.compare_at_price ? (variant.compare_at_price / 100).toFixed(0) : null;
    const hasDiscount = compareAtPrice && compareAtPrice > price;
    
    let html = '<div class="ProductItem__PriceList Heading">';
    
    if (hasDiscount) {
      const discount = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
      html += `<span class="ProductItem__Price Price Price--highlight Text--subdued" aria-label="Discount Price" tabindex="0" data-money-convertible="">${price} USD</span>`;
      html += ` <span class="ProductItem__Price Price Price--compareAt Text--subdued" aria-label="Full Price" data-money-convertible="">${compareAtPrice} USD</span>`;
      html += `<span class="discount" tabindex="0">Save ${discount}%</span>`;
    } else {
      html += `<span class="ProductItem__Price Price Text--subdued" tabindex="0" data-money-convertible="">${price} USD</span>`;
    }
    
    html += '</div>';
    return html;
  }

  /**
   * Handle color swatch click - update product images, price, and sizes
   * @param {Event} e - Click event
   */
  async function handleColorSwatchClick(e) {
    const swatch = e.target.closest('label.ColorSwatch');
    if (!swatch) return;
    
    const productContainer = swatch.closest('.ProductItem__Wrapper') || swatch.closest('.ProductItem');
    if (!productContainer) return;
    
    const productHandle = swatch.getAttribute('data-colorpart');
    if (!productHandle) return;
    
    // Remove active class from all swatches in this container and uncheck radios
    const swatchContainer = swatch.closest('.ProductItem__ColorSwatchList');
    if (swatchContainer) {
      swatchContainer.querySelectorAll('label.ColorSwatch').forEach(s => s.classList.remove('active'));
      swatchContainer.querySelectorAll('input.ColorSwatch__Radio').forEach(radio => radio.checked = false);
      swatch.classList.add('active');
    }
    
    // Get the radio input to get variant URL and image data, and check it
    const radioInput = swatch.previousElementSibling;
    if (radioInput && radioInput.type === 'radio') {
      radioInput.checked = true;
    }
    const variantUrl = radioInput ? radioInput.getAttribute('data-variant-url') : null;
    
    try {
      // Fetch product data
      const res = await fetch(`/products/${productHandle}.js`);
      if (!res.ok) throw new Error('Failed to fetch product');
      
      const productData = await res.json();
      
      // Update images
      const imageContainer = productContainer.querySelector('.AspectRatio');
      if (imageContainer && productData.images && productData.images.length > 0) {
        const imageLoader = imageContainer.querySelector('.Image__Loader');
        const loaderId = 'loader-' + productData.id + '-ys56y4teg';
        
        // Clear existing images but keep structure
        const existingImages = imageContainer.querySelectorAll('.product-slider-image');
        existingImages.forEach(img => img.remove());
        const existingNav = imageContainer.querySelectorAll('.product-image-nav');
        existingNav.forEach(btn => btn.remove());
        
        // Add all images
        productData.images.forEach((imageObj, index) => {
          let imageUrl, imageId, imageAspectRatio;
          
          if (typeof imageObj === 'string') {
            imageUrl = imageObj;
          } else if (imageObj && imageObj.src) {
            imageUrl = imageObj.src;
            imageId = imageObj.id || '';
            imageAspectRatio = imageObj.aspect_ratio || (imageObj.width && imageObj.height ? imageObj.width / imageObj.height : null);
          } else if (imageObj && imageObj.url) {
            imageUrl = imageObj.url;
            imageId = imageObj.id || '';
            imageAspectRatio = imageObj.aspect_ratio;
          }
          
          if (!imageUrl) return;
          
          const newImg = document.createElement('img');
          newImg.className = 'ProductItem__Image lazyautosizes product-slider-image';
          newImg.loading = index === 0 ? 'eager' : 'lazy';
          
          if (index === 0) {
            newImg.classList.add('Image--fadeIn', 'Image--lazyLoaded', 'is-active');
            newImg.onload = function() {
              this.classList.add('Image--lazyLoaded');
            };
          } else {
            newImg.classList.add('Image--fadeIn', 'is-hidden');
            newImg.onload = function() {
              this.classList.add('Image--lazyLoaded');
            };
          }
          
          newImg.src = imageUrl;
          newImg.alt = `${productData.title} Image ${index + 1}`;
          newImg.setAttribute('data-image-index', index);
          if (imageId) newImg.setAttribute('data-image-id', imageId);
          
          imageContainer.insertBefore(newImg, imageLoader);
          
          // Update aspect ratio from first image
          if (index === 0 && imageAspectRatio) {
            imageContainer.style.paddingBottom = `${100.0 / imageAspectRatio}%`;
            imageContainer.style.setProperty('--aspect-ratio', imageAspectRatio);
          }
        });
        
        // Add navigation arrows if more than 1 image
        if (productData.images.length > 1) {
          const prevBtn = document.createElement('button');
          prevBtn.className = 'product-image-nav product-image-nav--prev';
          prevBtn.type = 'button';
          prevBtn.setAttribute('aria-label', 'Previous image');
          prevBtn.innerHTML = '<svg data-testid="chevron-right-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 10" aria-hidden="true" width="10" fill="none" height="10" stroke-width="1" style="transform: scale(1.25) rotate(180deg);"><path d="M1 9.5L5 5L1 0.499999" stroke="#000" stroke-linecap="round"></path></svg>';
          
          const nextBtn = document.createElement('button');
          nextBtn.className = 'product-image-nav product-image-nav--next';
          nextBtn.type = 'button';
          nextBtn.setAttribute('aria-label', 'Next image');
          nextBtn.innerHTML = ' <svg data-testid="chevron-right-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 10" aria-hidden="true" width="10" fill="none" height="10" stroke-width="1" style="transform: scale(1.25);"><path d="M1 9.5L5 5L1 0.499999" stroke="#000" stroke-linecap="round"></path></svg>';
          
          imageContainer.insertBefore(prevBtn, imageLoader);
          imageContainer.insertBefore(nextBtn, imageLoader);
        }
        
        // Reset slider before re-initializing
        const existingSlider = productContainer.querySelector('.product-image-slider');
        if (existingSlider) {
          existingSlider.dataset.sliderInitialized = 'false';
          // Remove any existing dots
          const existingDots = existingSlider.querySelector('.product-slider-dots');
          if (existingDots) existingDots.remove();
        }
        
        // Initialize slider with new images
        setTimeout(function() {
          initImageSlider(productContainer);
        }, 100);
      }
      
      // Update product links
      const productUrl = `/products/${productData.handle}`;
      const imageLink = productContainer.querySelector('a.ProductItem__ImageWrapper');
      const titleLink = productContainer.querySelector('.ProductItem__Title a');
      
      if (imageLink) {
        imageLink.href = variantUrl || productUrl;
      }
      if (titleLink) {
        titleLink.href = variantUrl || productUrl;
        titleLink.textContent = productData.title;
      }
      
      // Update price
      const priceContainer = productContainer.querySelector('.ProductItem__PriceList');
      if (priceContainer) {
        const variant = (productData.variants && productData.variants.length > 0) ? productData.variants[0] : null;
        if (variant) {
          priceContainer.innerHTML = buildPrice(productData, variant);
        }
      }
      
      // Update sizes
      const sizeMount = productContainer.querySelector('.available-variant-size');
      if (sizeMount) {
        const sizesHtml = buildSizes(productData);
        if (sizesHtml) {
          // Find the VariantSizes container or replace the entire available-variant-size content
          const variantSizesContainer = sizeMount.querySelector('.VariantSizes');
          if (variantSizesContainer) {
            // Replace just the VariantSizes content
            variantSizesContainer.outerHTML = sizesHtml;
          } else {
            // Replace entire available-variant-size content
            sizeMount.innerHTML = sizesHtml;
          }
        }
      }
      
      // Update mobile button handle
      const mobileBtn = productContainer.querySelector('.show-size-for-mobile');
      if (mobileBtn) {
        mobileBtn.setAttribute('data-handle', productData.handle);
      }
      
    } catch (error) {
      console.error('[Product Boost Cart] Error updating product on swatch click:', error);
    }
  }

  // Track last processed click to prevent duplicates
  let lastClickTimeStamp = 0;
  
  /**
   * Handle show-size toggle click (same as boost-pfs-filter.js)
   * Toggles the size selector visibility
   * @param {Event} e - Click event
   */
  function handleShowSizeClick(e) {
    const btn = e.target.closest('.show-size-search');
    const mobileBtn = e.target.closest('.show-size-for-mobile');
    const resetAriaOnCard = (card) => {
      if (!card) return;
      const desktopBtn = card.querySelector('.show-size-search');
      const mobBtn = card.querySelector('.show-size-for-mobile');
      if (desktopBtn) desktopBtn.setAttribute('aria-expanded', 'false');
      if (mobBtn) mobBtn.setAttribute('aria-expanded', 'false');
  };
    
    // Handle show-size-search click
    if (btn) {
      // Prevent duplicate processing of the same click event
      if (e.timeStamp === lastClickTimeStamp) {
        return;
      }
      lastClickTimeStamp = e.timeStamp;
      
      const card = btn.closest('.ProductItem');
      if (!card) return;

      const sizeBox = card.querySelector('.available-variant-size');
      if (!sizeBox) return;

      // Find open box within the same container (search page, instant search, or collection page)
      const container = card.closest('[data-custom-search-results], [grid-search], .boost-sear-result-with-api, .CollectionInner__Products, .boost-pfs-filter-products');
      const openBox = container 
        ? container.querySelector('.available-variant-size.is-open')
        : document.querySelector('.available-variant-size.is-open');

      // Close any other open size boxes
      if (openBox && openBox !== sizeBox) {
        openBox.classList.remove('is-open');
        const prevCard = openBox.closest('.ProductItem');
        const prevIcon = prevCard?.querySelector('svg.icon.plus-minus');
        if (prevIcon) prevIcon.classList.remove('is-minus');
        const prevMobileBtn = prevCard?.querySelector('.show-size-for-mobile');
        if (prevMobileBtn) prevMobileBtn.classList.remove('is-active');
        resetAriaOnCard(prevCard);
      }

      // Toggle current size box
      sizeBox.classList.toggle('is-open');
      const icon = btn.querySelector('svg.icon.plus-minus');
      if (icon) icon.classList.toggle('is-minus');
      const isOpen = sizeBox.classList.contains('is-open');
      btn.setAttribute("aria-expanded", isOpen);
      return;
    }
    
    // Handle show-size-for-mobile button click
    if (mobileBtn) {
      e.preventDefault();
      const card = mobileBtn.closest('.ProductItem');
      if (!card) return;

      const sizeBox = card.querySelector('.available-variant-size');
      if (!sizeBox) return;

      // Find open box within the same container (search page, instant search, or collection page)
      const container = card.closest('[data-custom-search-results], [grid-search], .boost-sear-result-with-api, .CollectionInner__Products, .boost-pfs-filter-products');
      const openBox = container 
        ? container.querySelector('.available-variant-size.is-open')
        : document.querySelector('.available-variant-size.is-open');

      // Close any other open size boxes
      if (openBox && openBox !== sizeBox) {
        openBox.classList.remove('is-open');
        const prevCard = openBox.closest('.ProductItem');
        const prevIcon = prevCard?.querySelector('svg.icon.plus-minus');
        if (prevIcon) prevIcon.classList.remove('is-minus');
        const prevMobileBtn = prevCard?.querySelector('.show-size-for-mobile');
        if (prevMobileBtn) prevMobileBtn.classList.remove('is-active');
        resetAriaOnCard(prevCard);
      }

      // Toggle current size box
      sizeBox.classList.toggle('is-open');
      mobileBtn.classList.toggle('is-active');
      const isOpen = sizeBox.classList.contains('is-open');
      mobileBtn.setAttribute("aria-expanded", isOpen);
      return;
    }
    
    // Handle mobile click on open size box itself (mobile only)
    // Check if we're on mobile and clicking on the open size box container (not on size buttons)
    if (window.innerWidth <= 767) {
      const clickedSizeBox = e.target.closest('.available-variant-size.is-open');
      const clickedSizeButton = e.target.closest('.SizeSwatch');
      
      // If clicking on the size box container itself (not on size buttons), close it
      if (clickedSizeBox && !clickedSizeButton) {
        clickedSizeBox.classList.remove('is-open');
        const card = clickedSizeBox.closest('.ProductItem');
        if (card) {
          const icon = card.querySelector('svg.icon.plus-minus');
          if (icon) icon.classList.remove('is-minus');
          const mobileBtn = card.querySelector('.show-size-for-mobile');
          if (mobileBtn) mobileBtn.classList.remove('is-active');
          resetAriaOnCard(card);
        }
        return;
      }
    }
    
    // Close size box if clicking outside (check all containers)
    const allOpenBoxes = document.querySelectorAll('.available-variant-size.is-open');
    allOpenBoxes.forEach(function(openBox) {
      if (!e.target.closest('.available-variant-size') && 
          !e.target.closest('.show-size-search') && 
          !e.target.closest('.show-size-for-mobile')) {
        openBox.classList.remove('is-open');
        const card = openBox.closest('.ProductItem');
        
        if (card) {
            const activeIcon = card.querySelector('svg.icon.plus-minus');
            if (activeIcon) activeIcon.classList.remove('is-minus');
            const activeMobileBtn = card.querySelector('.show-size-for-mobile');
            if (activeMobileBtn) activeMobileBtn.classList.remove('is-active');
            
            resetAriaOnCard(card);
        }
      }
    });
  }

  /**
   * Initialize event listeners
   */
  function init() {
    // Guard against duplicate initialization
    if (window._productBoostCartInitialized) {
      return;
    }
    window._productBoostCartInitialized = true;
    
    // Use event delegation to handle dynamically loaded product cards
    document.addEventListener('click', handleSizeButtonClick);
    
    // Handle show-size toggle clicks (same functionality as boost-pfs-filter.js)
    document.addEventListener('click', handleShowSizeClick);
    
    // Handle color swatch clicks to update product images, price, and sizes
    document.addEventListener('click', handleColorSwatchClick);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose addVariantToCart globally if needed
  window.addVariantToCartBoost = addVariantToCart;

  /**
   * Initialize product image slider navigation
   * Supports infinite loop, dots on mobile, and swipe for search page and search drawer
   */
  function initImageSlider(container) {
    const slider = container.querySelector('.product-image-slider');
    if (!slider) return;

    // Check if slider is already initialized
    if (slider.dataset.sliderInitialized === 'true') return;
    slider.dataset.sliderInitialized = 'true';

    const images = Array.from(slider.querySelectorAll('.product-slider-image'));
    if (images.length <= 1) {
      const prevBtn = slider.querySelector('.product-image-nav--prev');
      const nextBtn = slider.querySelector('.product-image-nav--next');
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
      // Ensure single image is visible
      if (images.length === 1) {
        images[0].classList.add('is-active');
        images[0].classList.remove('is-hidden');
        images[0].style.opacity = '1';
      }
      return;
    }

    // Check if this slider is in search results page, search drawer, or collection page (enable infinite loop)
    const isInSearchContext = container.closest('[grid-search]') || 
                              container.closest('.boost-sear-result-with-api') ||
                              container.closest('[data-custom-search-results]') ||
                              container.closest('.search-page') ||
                              container.closest('.boost-pfs-filter-products') ||
                              container.closest('.CollectionInner__Products') ||
                              document.body.classList.contains('template-search') ||
                              document.body.classList.contains('template-collection');
    
    const enableInfiniteLoop = isInSearchContext;
    const isMobile = window.innerWidth <= 767;
    
    let currentIndex = 0;
    const prevBtn = slider.querySelector('.product-image-nav--prev');
    const nextBtn = slider.querySelector('.product-image-nav--next');

    // Create dots container for mobile in search context (always create for mobile)
    let dotsContainer = null;
    if (isInSearchContext && images.length > 1) {
      // Remove any existing dots
      const existingDots = slider.querySelector('.product-slider-dots');
      if (existingDots) existingDots.remove();
      
      dotsContainer = document.createElement('div');
      dotsContainer.className = 'product-slider-dots';
      
      // Calculate dot width based on number of images (100% / number of images)
      const dotWidth = (100 / images.length);
      
      for (let i = 0; i < images.length; i++) {
        const dot = document.createElement('span');
        dot.className = 'product-slider-dot' + (i === 0 ? ' is-active' : '');
        dot.dataset.index = i;
        dot.style.width = dotWidth + '%';
        dot.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          showImage(parseInt(this.dataset.index));
        });
        dotsContainer.appendChild(dot);
      }
      slider.appendChild(dotsContainer);
      
      // Add mobile class to slider for CSS styling
      slider.classList.add('has-dots');
    }

    function updateDots(index) {
      if (!dotsContainer) return;
      const dots = dotsContainer.querySelectorAll('.product-slider-dot');
      dots.forEach(function(dot, i) {
        dot.classList.toggle('is-active', i === index);
      });
    }

    function showImage(index) {
      // Handle infinite loop wrapping
      if (enableInfiniteLoop) {
        if (index < 0) {
          index = images.length - 1;
        } else if (index >= images.length) {
          index = 0;
        }
      } else {
        // Clamp index for non-infinite mode
        if (index < 0) index = 0;
        if (index >= images.length) index = images.length - 1;
      }

      const targetImage = images[index];
      if (!targetImage) return;
      
      // Get currently active image
      const activeImage = Array.from(images).find(function(img) {
        return img.classList.contains('is-active');
      });
      
      // Prepare new image first - remove hidden class and set higher z-index
      targetImage.classList.remove('is-hidden');
      targetImage.style.zIndex = '3';
      
      // Use double requestAnimationFrame to ensure DOM updates
      requestAnimationFrame(function() {
        requestAnimationFrame(function() {
          // Activate new image (this will fade it in via CSS)
          targetImage.classList.add('is-active');
          
          // Simultaneously deactivate old image (this will fade it out via CSS)
          if (activeImage && activeImage !== targetImage) {
            activeImage.classList.remove('is-active');
            activeImage.classList.add('is-hidden');
            activeImage.style.zIndex = '0';
          }
          
          // After transition completes, normalize z-index
          setTimeout(function() {
            targetImage.style.zIndex = '';
            if (activeImage) activeImage.style.zIndex = '';
          }, 450);
        });
      });

      // Update button states
      if (enableInfiniteLoop) {
        if (prevBtn) prevBtn.disabled = false;
        if (nextBtn) nextBtn.disabled = false;
      } else {
        if (prevBtn) prevBtn.disabled = index === 0;
        if (nextBtn) nextBtn.disabled = index === images.length - 1;
      }
      
      currentIndex = index;
      updateDots(index);
    }

    // Arrow button click handlers
    if (prevBtn) {
      prevBtn.onclick = function(e) {
        e.preventDefault();
        e.stopPropagation();
        showImage(currentIndex - 1);
      };
    }

    if (nextBtn) {
      nextBtn.onclick = function(e) {
        e.preventDefault();
        e.stopPropagation();
        showImage(currentIndex + 1);
      };
    }

    // Touch/Swipe support - same behavior as desktop arrow buttons
    if (isInSearchContext) {
      let touchStartX = 0;
      let touchEndX = 0;

      slider.addEventListener('touchstart', function(e) {
        touchStartX = e.touches[0].clientX;
        touchEndX = touchStartX;
      }, { passive: true });

      slider.addEventListener('touchmove', function(e) {
        touchEndX = e.touches[0].clientX;
      }, { passive: true });

      slider.addEventListener('touchend', function(e) {
        const deltaX = touchEndX - touchStartX;
        
        // Swipe left (finger moves left) = next image (same as clicking next button)
        // Swipe right (finger moves right) = previous image (same as clicking prev button)
        if (Math.abs(deltaX) > 30) {
          if (deltaX < 0) {
            // Swipe left - show next image
            showImage(currentIndex + 1);
          } else {
            // Swipe right - show previous image
            showImage(currentIndex - 1);
          }
        }
      }, { passive: true });
    }

    // Initialize first image - ensure it's visible
    const firstImage = images[0];
    if (firstImage) {
      // Make sure all images except first are hidden
      images.forEach(function(img, i) {
        if (i === 0) {
          img.classList.add('is-active');
          img.classList.remove('is-hidden');
          img.style.opacity = '1';
          img.style.zIndex = '2';
        } else {
          img.classList.remove('is-active');
          img.classList.add('is-hidden');
          img.style.opacity = '0';
        }
      });
      
      currentIndex = 0;
      
      if (enableInfiniteLoop) {
        if (prevBtn) prevBtn.disabled = false;
        if (nextBtn) nextBtn.disabled = false;
      } else {
        if (prevBtn) prevBtn.disabled = true;
        if (nextBtn) nextBtn.disabled = images.length <= 1;
      }
    }
  }

  /**
   * Initialize all image sliders on page
   */
  function initAllImageSliders() {
    document.querySelectorAll('.product-image-slider').forEach(slider => {
      initImageSlider(slider.closest('.ProductItem') || slider.closest('.ProductItem__Wrapper'));
    });
  }
  
  // Expose globally for use in other scripts
  window.initAllImageSliders = initAllImageSliders;
  window.initImageSlider = initImageSlider;

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllImageSliders);
  } else {
    initAllImageSliders();
  }

  // Re-initialize when search results are loaded
  document.addEventListener('search-results-loaded', function() {
    setTimeout(initAllImageSliders, 100);
  });

 })();

